import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../context/TimerContext';
import { CountdownTimer } from '../components/CountdownTimer';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { isSiteActive, loading: timerLoading } = useTimer();

  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && isSiteActive) {
      navigate('/products');
    }
  }, [isAuthenticated, isSiteActive, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !accessCode.trim()) {
      setError('Please enter both email and access code');
      return;
    }

    setLoading(true);

    try {
      await login(accessCode.trim().toUpperCase(), email.trim());
      navigate('/products');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Format access code as user types (uppercase)
  const handleAccessCodeChange = (value: string) => {
    setAccessCode(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 9));
  };

  if (timerLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo">
          <img src="/JackpointLogo.avif" alt="Jackpoint Jackstands" />
        </div>
        <h1>VIP Access</h1>
        <p className="login-subtitle">Enter your credentials to access exclusive products and discounts</p>

        <CountdownTimer className="login-timer" />

        {!isSiteActive ? (
          <div className="site-inactive">
            <p>The VIP site is currently not active.</p>
            <p>Please check back later or contact support.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={loading}
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="accessCode">Access Code</label>
              <input
                type="text"
                id="accessCode"
                value={accessCode}
                onChange={(e) => handleAccessCodeChange(e.target.value)}
                placeholder="ABC123XYZ"
                disabled={loading}
                autoComplete="off"
                maxLength={9}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading || !isSiteActive}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
